import { surname } from './model.js'

// What the page says about a member's fantasy league. P6-333a (GN-6a).
//
// Pure functions over three things the page already holds: the member's own blob (`3b/<id>/fantasy.json`, the bot's
// `utils/fantasy_projection.py`), tonight's cards (`3a/hoops/tonight.json`) and the box scores of tonight's started
// games (`3a/hoops/games/<id>.json`). Nothing here reads a clock, a zone or the network.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY NUMBER HERE IS OURS AND PROVISIONAL, AND THE PAGE SAYS SO IN WORDS (GN7)
// ═══════════════════════════════════════════════════════════════════════════
//
// The points are computed HERE, from the live box score and the scoring settings in the member's league copy, and
// Yahoo's own numbers replace them the next morning. Every label this file produces for a figure carries
// `PROVISIONAL`, and the components draw the word wherever a figure is drawn (the design's flag 7: *"provisional
// labelling is on every fantasy group heading"*). A total with no label would be the page asserting a score the league
// has not settled.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A PLAYER THE MAPPING COULD NOT MATCH HAS NO POINTS, AND IS NOT A ZERO
// ═══════════════════════════════════════════════════════════════════════════
//
// `person_id: null` is the bot saying *"we could not find this player in the NBA's data"*. He is "no live stats"
// (the design's dashed chip), he adds nothing to a total, and he is never given a figure: a `0.0` beside his name
// would read as "he has played and scored nothing". Likewise a rostered player whose box score line says he has not
// played has `null`, not `0` (`gameModel.js` on why).
//
// ⚠️ A BENCH SLOT IS NOT A SCORING SLOT. Yahoo does not count a benched or injured-list player's points, so the blob
// marks each `active` and only the active are lit, listed and summed. Showing a benched star's night in the total would be
// the page crediting points the league does not.

/** The word the figures carry. A constant so a test can assert it is drawn wherever a figure is. */
export const PROVISIONAL = 'provisional'

/**
 * 🔴 UNMEASURED: does Yahoo award `DD` AS WELL AS `TD` on a triple-double?
 *
 * `true` assumes it does (a triple-double is also a double-double, so a night with three categories at ten or more earns
 * both, 2 + 5 = 7 in Jordan's league). `false` assumes it does not (the night earns `TD` alone). Nobody has measured which
 * Yahoo does: the manager will compare a real triple-double against Yahoo's own total once a league is connected (P6-351),
 * and this one constant is then the only line that changes. `playerFpts` takes it as an option so both values are tested.
 */
export const TRIPLE_DOUBLE_ALSO_DOUBLE = true

/** The prefix the producer puts on a `field` that is derived from the line rather than read from it (`utils/fantasy_projection.py`). */
const DERIVED_PREFIX = 'derived:'

/** The five categories a double-double and a triple-double are counted over. Fouls, turnovers and shooting are not among them. */
export const DOUBLE_CATEGORIES = ['points', 'rebounds', 'assists', 'steals', 'blocks']

/** The count at which a category is "a double": ten, inclusive. */
export const DOUBLE_THRESHOLD = 10

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

/** The stats this blob scores, each `{ stat, field, value }`, with anything that cannot be computed left out. */
export function scoringStats(blob) {
  const stats = blob?.league?.scoring?.stats
  if (!Array.isArray(stats)) return []
  return stats.filter(
    (item) => isObject(item) && typeof item.field === 'string' && typeof item.stat === 'string' && Number.isFinite(item.value),
  )
}

/** Stats the league scores that a box score cannot give (a percentage): the totals leave them out and say so. */
export function unscoredStats(blob) {
  const list = blob?.league?.scoring?.unscored
  return Array.isArray(list) ? list.filter((item) => isObject(item) && typeof item.stat === 'string') : []
}

/**
 * How many of points, rebounds, assists, steals and blocks are ten or more, or `null` when the line is missing any of
 * the five (a double-double cannot be ruled in or out of a partial line, and a guess would read as a result).
 */
export function doubleCount(line) {
  let count = 0
  for (const field of DOUBLE_CATEGORIES) {
    const figure = line[field]
    if (!Number.isInteger(figure)) return null
    if (figure >= DOUBLE_THRESHOLD) count += 1
  }
  return count
}

/**
 * The value of one derived stat for a line: `1` or `0`, or `null` when the line cannot say.
 *
 * `TD` is 1 at a count of three or more. `DD` is 1 at a count of two or more, EXCEPT that when a triple-double is not
 * also a double-double (`tripleAlsoDouble` false) a count of three or more earns `TD` alone, so `DD` is 1 at exactly two.
 */
export function derivedFigure(field, line, { tripleAlsoDouble = TRIPLE_DOUBLE_ALSO_DOUBLE } = {}) {
  const count = doubleCount(line)
  if (count === null) return null
  if (field === `${DERIVED_PREFIX}triple_double`) return count >= 3 ? 1 : 0
  if (field === `${DERIVED_PREFIX}double_double`) return (tripleAlsoDouble ? count >= 2 : count === 2) ? 1 : 0
  return null // a derived stat this page does not know: no figure, never a guess
}

/**
 * Fantasy points for one box-score line, or `null` when there is no line to score.
 *
 * `null` for a player who has not played (`played !== true`) and for a line missing any figure the league scores. A
 * partial line scored as if the missing figure were zero would be a guess that reads as a result. A `derived:` field
 * (`DD`, `TD`) is computed from the line by `derivedFigure`; a player with no line yet has `null`, so scores neither.
 */
export function playerFpts(line, stats, options = {}) {
  if (!isObject(line) || line.played !== true) return null
  let total = 0
  for (const { field, value } of stats) {
    const figure = field.startsWith(DERIVED_PREFIX) ? derivedFigure(field, line, options) : line[field]
    if (!Number.isInteger(figure)) return null
    total += figure * value
  }
  return total
}

/** One decimal, the design's `toFixed(1)`; `–` for no figure. A hundredth is never printed, so no `-0.0`. */
export function fptsText(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–'
  const text = value.toFixed(1)
  return text === '-0.0' ? '0.0' : text
}

/**
 * Where each NBA person is on tonight's started games: `Map(person_id -> { gameId, status, side, line })`.
 *
 * `boxes` is `Map(gameId -> the game's own object)`. The status is the BOARD card's when there is one (the card is the
 * fresher word on whether a game is still being played), else the box's own.
 */
export function indexLines(boxes, cards) {
  const index = new Map()
  if (!(boxes instanceof Map)) return index
  const statusOf = new Map((Array.isArray(cards) ? cards : []).map((card) => [card?.id, card?.status]))
  for (const [gameId, game] of boxes) {
    if (!isObject(game) || !isObject(game.players)) continue
    const status = statusOf.get(gameId) ?? game.status
    for (const side of ['away', 'home']) {
      for (const line of Array.isArray(game.players[side]) ? game.players[side] : []) {
        if (Number.isInteger(line?.person_id)) index.set(line.person_id, { gameId, status, side, line })
      }
    }
  }
  return index
}

/** The tricodes that play tonight, by card status. */
function tricodes(cards) {
  const out = new Map()
  for (const card of Array.isArray(cards) ? cards : []) {
    for (const side of ['away', 'home']) {
      const code = card?.[side]?.tricode
      if (typeof code === 'string') out.set(code, card)
    }
  }
  return out
}

/**
 * One rostered player tonight: what he is, where he is, and what he has scored.
 *
 * `kind`:
 *   `played`   a line that says he has played: `fpts` is a number and `onFloor` says whether he is out there now
 *   `waiting`  on a started game's roster and not yet in it: no figure
 *   `dnp`      the feed lists him as not playing: not drawn (the design leaves him out of the chips)
 *   `pre`      his game has not tipped (known only when the mapping gave his team)
 *   `nolive`   UNMAPPED: no figure, ever, drawn as the dashed "no live stats" chip
 *   `idle`     nothing says he plays tonight: not drawn
 */
export function playerTonight(player, { stats, index, byCode }) {
  const base = { name: player.name, team: typeof player.team === 'string' ? player.team : null, fpts: null, onFloor: false }
  if (player.person_id === null || player.person_id === undefined) {
    // Unmapped. If we know his team and it is not playing tonight he is idle (nothing to say); if we do not know his
    // team we cannot say that, so he is listed rather than hidden.
    const card = base.team === null ? null : (byCode.get(base.team) ?? null)
    if (base.team !== null && card === null) return { ...base, kind: 'idle', gameId: null }
    return { ...base, kind: 'nolive', gameId: card?.id ?? null }
  }
  const found = index.get(player.person_id)
  if (found) {
    const { line, gameId, status, side } = found
    if (line.played === true) {
      return { ...base, kind: 'played', gameId, side, fpts: playerFpts(line, stats), onFloor: line.oncourt === true && status === 'live' }
    }
    if (typeof line.not_playing_reason === 'string' && line.not_playing_reason !== '') return { ...base, kind: 'dnp', gameId, side }
    return { ...base, kind: 'waiting', gameId, side }
  }
  const card = base.team === null ? null : (byCode.get(base.team) ?? null)
  if (card !== null && card.status === 'pregame') return { ...base, kind: 'pre', gameId: card.id }
  return { ...base, kind: 'idle', gameId: card?.id ?? null }
}

/** Every ACTIVE player on a roster, as `playerTonight` says. */
export function rosterTonight(roster, context) {
  return (Array.isArray(roster) ? roster : []).filter((player) => player?.active === true).map((player) => playerTonight(player, context))
}

/** The context every call above shares, built once per render of a board. */
export function tonightContext(blob, boxes, cards) {
  return { stats: scoringStats(blob), index: indexLines(boxes, cards), byCode: tricodes(cards) }
}

/** A team's provisional total tonight: the sum of its active players' computed points. Unmapped add nothing. */
export function teamTotal(players) {
  let total = 0
  for (const player of players) if (player.kind === 'played' && player.fpts !== null) total += player.fpts
  return total
}

/**
 * The crew board: every linked team in the viewer's league, ordered by tonight's total, highest first.
 *
 * Ties break on the team's name (case-folded), so the order is the same on every render and on every viewer's screen.
 * `you` is the blob's own mark, never a comparison of names.
 */
export function crewBoard(blob, boxes, cards) {
  const context = tonightContext(blob, boxes, cards)
  const rows = (Array.isArray(blob?.crew) ? blob.crew : [])
    .filter((team) => isObject(team) && typeof team.name === 'string')
    .map((team) => {
      const players = rosterTonight(team.roster, context)
      return {
        name: team.name,
        you: team.you === true,
        total: teamTotal(players),
        played: players.filter((player) => player.kind === 'played').length,
        onFloor: players.filter((player) => player.onFloor).length,
      }
    })
  rows.sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
  return rows.map((row, index) => ({ ...row, rank: index + 1 }))
}

/** The viewer's players on one card, as chips: those that belong to this game and are worth drawing. */
export function chipsFor(blob, boxes, cards, cardId) {
  const context = tonightContext(blob, boxes, cards)
  return rosterTonight(blob?.you?.roster, context)
    .filter((player) => player.gameId === cardId && ['played', 'waiting', 'pre', 'nolive'].includes(player.kind))
    .map((player) => ({ ...player, short: surname(player.name) || player.name }))
}

/** The viewer's own night, for the panel. `null` when there is nothing for a panel to say (no games tonight). */
export function panelModel(blob, boxes, cards) {
  if (!isObject(blob) || !Array.isArray(cards) || cards.length === 0) return null
  const context = tonightContext(blob, boxes, cards)
  const mine = rosterTonight(blob?.you?.roster, context)
  const playing = mine.filter((player) => ['played', 'waiting', 'pre'].includes(player.kind))
  return {
    total: teamTotal(mine),
    playing: playing.length,
    onFloor: mine.filter((player) => player.onFloor).length,
    yetToTip: mine.filter((player) => player.kind === 'pre').length,
    noLive: mine.filter((player) => player.kind === 'nolive'),
    crew: crewBoard(blob, boxes, cards),
    unscored: unscoredStats(blob).map((item) => item.stat),
    league: leagueLine(blob),
    leagueName: typeof blob?.league?.name === 'string' ? blob.league.name : '',
  }
}

/** `Couch League · points · PTS 1 · REB 1.2 · TO −1`, the design's line. */
export function leagueLine(blob) {
  const name = typeof blob?.league?.name === 'string' ? blob.league.name : ''
  const parts = scoringStats(blob).map(({ stat, value }) => `${stat} ${value > 0 ? value : `−${Math.abs(value)}`}`)
  return [name, 'points', ...parts].filter((part) => part !== '').join(' · ')
}

/** `3 of yours play tonight · 2 on the floor · 1 yet to tip`, the design's count line. */
export function countLine(model) {
  const parts = [`${model.playing} of yours play tonight`, `${model.onFloor} on the floor`]
  if (model.yetToTip > 0) parts.push(`${model.yetToTip} yet to tip`)
  return parts.join(' · ')
}

/** The Fpts of one box-score player under this league's scoring, as the column prints it. */
export function boxFpts(player, stats) {
  return playerFpts(player, stats)
}

/** The set of person ids on the viewer's active roster, for lighting rows in a box score. */
export function mineIds(blob) {
  const ids = new Set()
  for (const player of Array.isArray(blob?.you?.roster) ? blob.you.roster : []) {
    if (player?.active === true && Number.isInteger(player.person_id)) ids.add(player.person_id)
  }
  return ids
}
